/**
 * Computer Control Controller
 *
 * Manages state and gateway RPC calls for OS-level computer control admin panel.
 */

// --------------- Types ---------------

export type ComputerStatus = {
  enabled: boolean;
  display: {
    running: boolean;
    displayId: string | null;
    resolution: string | null;
  };
  vnc: {
    running: boolean;
    port: number | null;
  };
  activeSessions: number;
};

export type ComputerConfig = {
  enabled: boolean;
  displayResolution: string;
  colorDepth: number;
  vncPort: number;
};

// --------------- State ---------------

export interface ComputerState {
  client: { request<T>(method: string, params: unknown): Promise<T> } | null;
  connected: boolean;

  computerStatus: ComputerStatus | null;
  computerConfig: ComputerConfig | null;
  computerLoading: boolean;
  computerError: string | null;
  computerScreenshot: string | null;
  computerScreenshotLoading: boolean;
}

export const COMPUTER_STATE_DEFAULTS: Omit<ComputerState, "client" | "connected"> = {
  computerStatus: null,
  computerConfig: null,
  computerLoading: false,
  computerError: null,
  computerScreenshot: null,
  computerScreenshotLoading: false,
};

// --------------- Helpers ---------------

function friendlyError(err: unknown): string {
  const msg = String(err).toLowerCase();
  if (msg.includes("method not found") || msg.includes("not registered")) {
    return "Computer control methods not available. Ensure the igloobot-core plugin is loaded with enableComputer: true.";
  }
  return String(err);
}

// --------------- Loaders ---------------

type StatusResult = { ok: boolean; status?: ComputerStatus; error?: string };
type ConfigResult = { ok: boolean; config?: ComputerConfig; error?: string };
type ScreenshotResult = { ok: boolean; image?: string; error?: string };

export async function loadComputerStatus(state: ComputerState): Promise<void> {
  if (!state.client || !state.connected) {
    return;
  }
  state.computerLoading = true;
  state.computerError = null;
  try {
    const res = await state.client.request<StatusResult>("igloobot.computer.status", {});
    if (res.ok && res.status) {
      state.computerStatus = res.status;
    } else {
      state.computerError = res.error ?? "Failed to load computer status";
    }
  } catch (err) {
    state.computerError = friendlyError(err);
  } finally {
    state.computerLoading = false;
  }
}

export async function loadComputerConfig(state: ComputerState): Promise<void> {
  if (!state.client || !state.connected) {
    return;
  }
  try {
    const res = await state.client.request<ConfigResult>("igloobot.computer.config.get", {});
    if (res.ok && res.config) {
      state.computerConfig = res.config;
    }
  } catch {
    // Config might not be available if computer control is disabled
  }
}

export async function takeScreenshot(state: ComputerState): Promise<void> {
  if (!state.client || !state.connected) {
    return;
  }
  state.computerScreenshotLoading = true;
  try {
    const res = await state.client.request<ScreenshotResult>("igloobot.computer.screenshot", {});
    if (res.ok && res.image) {
      state.computerScreenshot = res.image;
    } else {
      state.computerError = res.error ?? "Failed to take screenshot";
    }
  } catch (err) {
    state.computerError = friendlyError(err);
  } finally {
    state.computerScreenshotLoading = false;
  }
}

export async function loadComputerTab(state: ComputerState): Promise<void> {
  await Promise.all([loadComputerStatus(state), loadComputerConfig(state)]);
}
